import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Button,
  ButtonGroup,
  TextContainer,
  Text,
  Banner,
  ProgressBar,
  Stack,
  Heading,
  DataTable,
  Spinner,
  Badge
} from '@shopify/polaris';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, query, getDocs, getCountFromServer } from 'firebase/firestore';
import { runCompleteMigration } from '../services/trackingMigrationService';

function TrackingAdmin() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [migrationError, setMigrationError] = useState(null);

  useEffect(() => {
    loadTrackingStats();
  }, []);

  const loadTrackingStats = async () => {
    try {
      setLoading(true);
      
      // Get document counts for each collection
      const creditUsageCount = await getCollectionCount('credit_usage');
      const creditUsageBatchesCount = await getCollectionCount('credit_usage_batches');
      const creditUsageStatsCount = await getCollectionCount('credit_usage_stats');
      const shopsCount = await getCollectionCount('shops');
      
      // Get total credit usage by type
      const creditUsageByType = await getCreditUsageByType();
      
      setStats({
        documentCounts: {
          credit_usage: creditUsageCount,
          credit_usage_batches: creditUsageBatchesCount,
          credit_usage_stats: creditUsageStatsCount,
          shops: shopsCount
        },
        creditUsageByType
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading tracking stats:', error);
      setLoading(false);
    }
  };

  const getCollectionCount = async (collectionName) => {
    const coll = collection(db, collectionName);
    const snapshot = await getCountFromServer(coll);
    return snapshot.data().count;
  };

  const getCreditUsageByType = async () => {
    try {
      // Query a sample of credit usage records
      const q = query(collection(db, 'credit_usage'));
      const querySnapshot = await getDocs(q);
      
      // Initialize counters
      const counts = {
        template: 0,
        image: 0,
        sizechart: 0,
        other: 0
      };
      
      // Process each document
      querySnapshot.forEach(doc => {
        const usage = doc.data();
        let usageType = usage.usageType || 'other';
        
        // Determine usage type if not explicitly set
        if (!usageType || usageType === 'other') {
          if (usage.type === 'sizechart_generation' || usage.type === 'sizechart_comparison') {
            usageType = 'sizechart';
          } else if (usage.templateId) {
            usageType = 'template';
          } else if (usage.type === 'image_generation' || usage.type === 'image_processing') {
            usageType = 'image';
          }
        }
        
        // Increment the appropriate counter
        counts[usageType] = (counts[usageType] || 0) + 1;
      });
      
      return counts;
    } catch (error) {
      console.error('Error getting credit usage by type:', error);
      return { template: 0, image: 0, sizechart: 0, other: 0 };
    }
  };

  const runMigration = async () => {
    try {
      setMigrationRunning(true);
      setMigrationResult(null);
      setMigrationError(null);
      
      // Run the migration
      const result = await runCompleteMigration();
      
      if (result.success) {
        setMigrationResult(result);
      } else {
        setMigrationError(result.error);
      }
      
      // Reload stats after migration
      await loadTrackingStats();
      
      setMigrationRunning(false);
    } catch (error) {
      console.error('Error running migration:', error);
      setMigrationError(error.message);
      setMigrationRunning(false);
    }
  };

  if (loading) {
    return (
      <Page title="Tracking System Admin">
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <div style={{ marginTop: '1rem' }}>
                  <Text variant="bodyMd">Loading tracking system stats...</Text>
                </div>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page 
      title="Tracking System Admin"
      subtitle="Optimize your tracking system and reduce Firestore document count"
    >
      <Layout>
        {/* Current System Stats */}
        <Layout.Section>
          <Card title="Current Tracking System Stats">
            <Card.Section>
              <Stack distribution="fillEvenly">
                <Stack.Item>
                  <TextContainer>
                    <Heading>Document Counts</Heading>
                    <DataTable
                      columnContentTypes={['text', 'numeric']}
                      headings={['Collection', 'Documents']}
                      rows={[
                        ['Credit Usage Records', stats?.documentCounts?.credit_usage || 0],
                        ['Credit Usage Batches', stats?.documentCounts?.credit_usage_batches || 0],
                        ['Credit Usage Stats', stats?.documentCounts?.credit_usage_stats || 0],
                        ['Shops', stats?.documentCounts?.shops || 0]
                      ]}
                    />
                  </TextContainer>
                </Stack.Item>
                <Stack.Item>
                  <TextContainer>
                    <Heading>Credit Usage by Type</Heading>
                    <DataTable
                      columnContentTypes={['text', 'numeric']}
                      headings={['Type', 'Count']}
                      rows={[
                        ['Templates', stats?.creditUsageByType?.template || 0],
                        ['Image Processing', stats?.creditUsageByType?.image || 0],
                        ['Size Charts', stats?.creditUsageByType?.sizechart || 0],
                        ['Other', stats?.creditUsageByType?.other || 0]
                      ]}
                    />
                  </TextContainer>
                </Stack.Item>
              </Stack>
            </Card.Section>
            <Card.Section>
              <TextContainer>
                <Text variant="bodyMd">
                  Your tracking system currently uses <strong>{stats?.documentCounts?.credit_usage || 0}</strong> individual documents to track credit usage. 
                  The optimized system will batch these into approximately <strong>{Math.ceil((stats?.documentCounts?.credit_usage || 0) / 50)}</strong> documents, 
                  significantly reducing Firestore reads and writes.
                </Text>
              </TextContainer>
            </Card.Section>
          </Card>
        </Layout.Section>
        
        {/* Migration Section */}
        <Layout.Section>
          <Card title="Tracking System Optimization">
            <Card.Section>
              <Banner
                title="This is a one-time operation"
                status="warning"
              >
                <p>This migration will consolidate your tracking data to reduce Firestore document count while preserving all historical data. It's recommended to run this during off-peak hours.</p>
              </Banner>
              
              <div style={{ marginTop: '1rem' }}>
                <TextContainer>
                  <Text variant="headingMd">The migration will:</Text>
                  <ul>
                    <li>Consolidate {stats?.documentCounts?.credit_usage || 0} credit usage records into batched documents</li>
                    <li>Generate aggregated credit usage statistics for faster dashboard loading</li>
                    <li>Optimize shop metrics to reduce redundancy</li>
                  </ul>
                </TextContainer>
              </div>
            </Card.Section>
            
            <Card.Section>
              <ButtonGroup>
                <Button
                  primary
                  loading={migrationRunning}
                  disabled={migrationRunning}
                  onClick={runMigration}
                >
                  Run Migration
                </Button>
                <Button onClick={loadTrackingStats} disabled={migrationRunning}>
                  Refresh Stats
                </Button>
              </ButtonGroup>
            </Card.Section>
            
            {migrationRunning && (
              <Card.Section>
                <TextContainer>
                  <Text variant="bodyMd">Migration in progress... This may take several minutes.</Text>
                  <div style={{ marginTop: '1rem' }}>
                    <ProgressBar progress={100} size="small" color="primary" />
                  </div>
                </TextContainer>
              </Card.Section>
            )}
            
            {migrationResult && (
              <Card.Section>
                <Banner
                  title="Migration completed successfully"
                  status="success"
                >
                  <p>The tracking system has been optimized. Here's a summary of the changes:</p>
                  <ul>
                    <li><strong>{migrationResult.creditMigration.migrated}</strong> credit usage records migrated to <strong>{migrationResult.creditMigration.batches}</strong> batch documents</li>
                    <li><strong>{migrationResult.statsGeneration.users}</strong> users' credit usage statistics generated</li>
                  </ul>
                </Banner>
              </Card.Section>
            )}
            
            {migrationError && (
              <Card.Section>
                <Banner
                  title="Migration failed"
                  status="critical"
                >
                  <p>An error occurred during the migration: {migrationError}</p>
                </Banner>
              </Card.Section>
            )}
          </Card>
        </Layout.Section>
        
        {/* Implementation Guide */}
        <Layout.Section>
          <Card title="Implementation Guide">
            <Card.Section>
              <TextContainer>
                <Text variant="headingMd">How to Use the Optimized Tracking System</Text>
                <p>After running the migration, update your code to use the new optimized services:</p>
                <ol>
                  <li>Replace <code>creditService.js</code> imports with <code>optimizedCreditService.js</code></li>
                  <li>Replace <code>shopMetricsService.js</code> imports with <code>optimizedShopMetricsService.js</code></li>
                  <li>Update <code>Dashboard.js</code> to use the new <code>getCreditUsageStats</code> function</li>
                </ol>
                <Text variant="headingMd" style={{ marginTop: '1rem' }}>Benefits</Text>
                <ul>
                  <li><strong>Reduced Firestore costs</strong> - Fewer document reads and writes</li>
                  <li><strong>Faster dashboard loading</strong> - Pre-aggregated statistics</li>
                  <li><strong>Simplified analytics</strong> - Consolidated data structure</li>
                  <li><strong>Maintained functionality</strong> - All existing features work with the new system</li>
                </ul>
              </TextContainer>
            </Card.Section>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default TrackingAdmin;
